//CRN_20260508 - Riskline Collections service (countries / regions / cities / airports)
//             - Cached in memory after first load to avoid refetching on dropdown re-open
//CRN_20260513 - https://riskline.readme.io/reference/apis-overview

import { catchError, map, Observable, of, throwError } from 'rxjs';
import { eSimApiPaths } from '../../consts';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from '../utelize/general/exceptions-service';
import { HttpClientService } from '../utelize/general/http-client-service';
import { Injectable } from '@angular/core';
import { RisklineAirportDTO, RisklineCityDTO, RisklineCountryDTO, RisklineRegionDTO } from '../../models/system/riskline/riskline-collections.dto';

@Injectable({ providedIn: 'root' })
export class RisklineCollectionsService {

  private airportsCache : RisklineAirportDTO[] | undefined;
  private citiesCache   : RisklineCityDTO[]    | undefined;
  private countriesCache: RisklineCountryDTO[] | undefined;
  private regionsCache  : RisklineRegionDTO[]  | undefined;

  constructor(
    private exceptionsService: ExceptionsService,
    private httpClientService: HttpClientService
  ) { }

  public clearCache(): void {
    try {
      this.airportsCache =undefined;
      this.citiesCache   =undefined;
      this.countriesCache=undefined;
      this.regionsCache  =undefined;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getAirports(countryIsoCode?: string): Observable<RisklineAirportDTO[]> {
    try {
      if (this.airportsCache!=undefined)
        return of(this.filterByCountry(this.airportsCache, countryIsoCode));

      return this.httpClientService
             .get<RisklineAirportDTO[]>(environment.eSimApi +
                                        eSimApiPaths.RiskIntelligence + '/' +
                                        eSimApiPaths.RiskIntelligenceCollections +
                                        '/airports')
             .pipe(
               map(data => {
                 this.airportsCache=data ?? [];
                 return this.filterByCountry(this.airportsCache, countryIsoCode);
               }),
               catchError((error) => {
                 return throwError(() => new Error('RisklineCollectionsService.getAirports() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  public getCities(countryIsoCode?: string): Observable<RisklineCityDTO[]> {
    try {
      if (this.citiesCache!=undefined)
        return of(this.filterByCountry(this.citiesCache, countryIsoCode));

      return this.httpClientService
             .get<RisklineCityDTO[]>(environment.eSimApi +
                                     eSimApiPaths.RiskIntelligence + '/' +
                                     eSimApiPaths.RiskIntelligenceCollections + '/cities')
             .pipe(
               map(data => {
                 this.citiesCache=data ?? [];
                 return this.filterByCountry(this.citiesCache, countryIsoCode);
               }),
               catchError((error) => {
                 return throwError(() => new Error('RisklineCollectionsService.getCities() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  public getCountries(): Observable<RisklineCountryDTO[]> {
    try {
      if (this.countriesCache!=undefined)
        return of(this.countriesCache);

      return this.httpClientService
             .get<RisklineCountryDTO[]>(environment.eSimApi +
                                        eSimApiPaths.RiskIntelligence + '/' +
                                        eSimApiPaths.RiskIntelligenceCollections +
                                        '/countries')
             .pipe(
               map(data => {
                 const countries=data ?? [];

                 if (countries.length>0)
                   this.countriesCache=countries;

                 return countries;
               }),
               catchError((error) => {
                 return throwError(() => new Error('RisklineCollectionsService.getCountries() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  public getRegions(countryIsoCode?: string): Observable<RisklineRegionDTO[]> {
    try {
      if (this.regionsCache!=undefined)
        return of(this.filterByCountry(this.regionsCache, countryIsoCode));

      return this.httpClientService
             .get<RisklineRegionDTO[]>(environment.eSimApi +
                                       eSimApiPaths.RiskIntelligence + '/' +
                                       eSimApiPaths.RiskIntelligenceCollections +
                                       '/regions')
             .pipe(
               map(data => {
                 this.regionsCache=data ?? [];
                 return this.filterByCountry(this.regionsCache, countryIsoCode);
               }),
               catchError((error) => {
                 return throwError(() => new Error('RisklineCollectionsService.getRegions() - ' + this.exceptionsService.formatHttpError(error)));
               })
             );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return of([]);
    }
  }

  private filterByCountry<T extends { country_iso_code: string }>(items: T[], countryIsoCode: string | undefined): T[] {
    try {
      if (!countryIsoCode || countryIsoCode.trim()=='')
        return items;

      const iso=countryIsoCode.toUpperCase();
      
      return items.filter(x => (x.country_iso_code ?? '').toUpperCase()==iso);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return [];
    }
  }
}
